import { Field as FormikField, Formik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { MdAssignmentInd, MdPermIdentity, MdCheck } from "react-icons/md";
import * as Yup from "yup";
import ContainedButton from "components/layout/form/containedButton";
import OutlinedButton from "components/layout/form/outlinedButton";
import TextField from "components/layout/form/textField";
import api from "services/api";
import theme from "styles/theme";
import { banks } from "./banks";
import { normalizeDocumentForPayload } from "utils/formatting/document";
import {
  applyCpfCnpjInputMask,
  getCpfCnpjLoginFieldError,
} from "utils/validation/cpfCnpjLoginField";
import { toast } from "react-toastify";
import { updateSelectedUnit, setUnitsList } from "store/actions/select-unit-actions";
import { useDispatch, useSelector } from "react-redux";

import {
  BankContainer,
  BankLabel,
  BankName,
  BanksContainer,
  ButtonsContainer,
  FormContainer,
  InputsContainer,
  Alert,
  NumberDigit,
  BanksHeader,
  FieldContainer,
  Check,
  Error
} from "./styles";

const DirectDebitForm = ({
  confirmButtonText = "Concluir",
  cancelButtonText = "Cancelar",
  handleSubmitErrorAction,
  handleSubmitAction = () => { },
  handleCancelAction = () => { },
  unit,
  formInfomation = {},
  setSelectedBank = () => { }
}) => {
  const [loading, setLoading] = useState(false);
  const [banksList, setBanksList] = useState([]);
  const [bankValue, setBankValue] = useState(formInfomation.bankId);
  const dispatch = useDispatch();
  const unitsList = useSelector(state => state.selectUnitReducer.unitsList);


  const getBanks = useCallback(() => {
    const access_token = localStorage.getItem(
      "@UltragazEmpresarial:accessToken"
    );
    const document = localStorage.getItem("@UltragazEmpresarial:document");
    const headers = {
      access_token
    };
    const params = {
      document,
      _expand: "installments",
      _offset: 0,
      _limit: 3
    };
    api.get(`industrial/bill/v1/banks`, { params, headers }).then(res => {
      const responseData = res.data || [];
      setBanksList(responseData?.map((bank) => {
        return ({
          ...bank,
          mensagem: banks?.find((bankMok) => bankMok?.code === bank?.code)?.mensagem,
          icone: banks?.find((bankMok) => bankMok?.code === bank?.code)?.icone,
        });
      }));
    });
  }, [api]);

  useEffect(() => {
    getBanks();
  }, []);

  const patchUnitDebit = useCallback(async ({ bankId, branch, account, accountCheckDigit, accountDocument, accountOwnerName }) => {
    accountDocument = normalizeDocumentForPayload(accountDocument);
    try {
      setLoading(true);
      const headers = {
        access_token: localStorage.getItem("@UltragazEmpresarial:accessToken")
      };
      const body = {
        hasDirectDebit: true,
        bankId,
        branch,
        account,
        accountCheckDigit: accountCheckDigit?.length ? accountCheckDigit : null,
        accountDocument,
        accountOwnerName
      };

      await api.patch(`industrial/customer/v1/customers/me/units/${unit.unitId}`, body, {
        headers
      });
      dispatch(updateSelectedUnit({
        account,
        accountCheckDigit,
        accountDocument,
        accountOwnerName,
        bankId,
        branch,
        hasDirectDebit: true
      }));

      if (unitsList.length) {
        const unitsListArray = unitsList.map(u => {
          if (u.unitId === unit.unitId) {
            u = {
              ...u,
              account,
              accountCheckDigit,
              accountDocument,
              accountOwnerName,
              bankId,
              branch,
              hasDirectDebit: true
            }
          }
          return u;
        });
        dispatch(setUnitsList(unitsListArray));
      }

      setLoading(false);
      setSelectedBank(bankId);
      handleSubmitAction();
    } catch (err) {
      setLoading(false);
      const erro = err.response;
      if (erro) {
        if (!handleSubmitErrorAction) {
          const message = err.response.data.message;
          if (err.response.status !== 401) {
            toast.error(message);
          }
        } else {
          handleSubmitErrorAction(err);
        }
      } else {
        if (err.response.status !== 401) {
          toast.error("Ocorreu um erro interno na aplicação");
        }
      }
    }
  }, [unit, handleSubmitAction, setLoading, toast, dispatch, unitsList, handleSubmitErrorAction, setSelectedBank]);

  const validatorSchema = () =>
    Yup.object().shape({
      bankId: Yup.string().required("É necessário selecionar um banco"),
      accountOwnerName: Yup.string()
        .trim()
        .required("É necessário digitar o nome do titular da conta."),
      accountDocument: Yup.string()
        .trim()
        .required("É necessário digitar o CPF do titular da conta."),
      branch: Yup.string()
        .trim()
        .max(4, 'Digite no máximo 4 dígitos.')
        .required("É necessário digitar o número da agência."),
      account: Yup.string()
        .trim()
        .when('bankId', {
          is: (bankId) => bankId === '748',
          then: Yup.string()
            .matches(/^\d{5}$/, "A conta corrente do Sicredi deve ter 5 números e 1 dígito (ex: 12345-6)")
            .required("É necessário digitar o número da conta."),
          otherwise: Yup.string()
            .max(20, 'Digite no máximo 20 dígitos.')
            .required("É necessário digitar o número da conta."),
        }),
      accountCheckDigit: Yup.string()
        .trim()
        .max(1, 'Digite no máximo 1 dígito.')
        .test('bankId', 'É necessário inserir o número do dígito da conta.', (value) => {
          if (bankValue === '001' || !!value) return true;
          return false;
        }),
    });

  const handleName = useCallback((e, handleChange) => {
    const event = e;
    const letters = event.target.value
      .replace(/[0-9]/g, "")
      .replace(/[^\w\s]/gi, "");
    event.target.value = letters;
    handleChange(event);
  });

  const handleNumber = (e, handleChange) => {
    const event = e;
    if (event.target.value.length <= 20) {
      const numbers = event.target.value.replace(/[^\d]/g, "");
      event.target.value = numbers;
      handleChange(event);
    }
  };

  const handleNumberDigit = (e, handleChange) => {
    const event = e;
    if (event.target.value.length <= 1) {
      const numbers = event.target.value.replace(/[^\d]/g, "");
      event.target.value = numbers;
      handleChange(event);
    }
  };

  const handleAgencia = (e, handleChange) => {
    const event = e;
    if (event.target.value.length <= 4) {
      const numbers = event.target.value.replace(/[^\d]/g, "");
      event.target.value = numbers;
      handleChange(event);
    }
  };

  const handleBank = (e, handleChange) => {
    setBankValue(e.target.value);
    handleChange(e);
  };

  const handleDocument = (event, handleChange) => {
    applyCpfCnpjInputMask(event);
    handleChange(event);
  };

  const validateDocument = value => getCpfCnpjLoginFieldError(value);

  const handleSubmit = useCallback(values => {
    if (!loading) {
      patchUnitDebit(values);
    }
  }, [patchUnitDebit, loading]);

  return (
    <>
      <BanksHeader>
        <h1>Selecione seu banco abaixo</h1>
        <h2>
          Para outros bancos, acesse o canal digital do banco ou caixa
          eletrônico tendo em mãos um dos nossos boletos
        </h2>
      </BanksHeader>
      <Formik
        initialValues={{
          bankId: formInfomation.bankId || "",
          accountOwnerName: formInfomation.accountOwnerName || "",
          accountDocument: formInfomation.accountDocument || "",
          branch: formInfomation.branch || "",
          accountCheckDigit: formInfomation.accountCheckDigit || "",
          account: formInfomation.account || ""
        }}
        onSubmit={handleSubmit}
        validationSchema={validatorSchema}
        render={({
          handleChange,
          handleBlur,
          values,
          errors,
          touched,
          validateForm,
          isValid,
          dirty
        }) => {
          useEffect(() => {
              validateForm();
          }, [values.bankId]);
          return (
            <FormContainer>
              <div>
                <BanksContainer>
                  {banksList.map(({ code, name, icone }) => (
                    <BankContainer key={code}>
                      {code === values.bankId && (
                        <Check>
                          <MdCheck size={15} />
                        </Check>
                      )}
                      <BankLabel
                        selected={code === values.bankId}
                        image={icone}
                        htmlFor={"bankCode" + code}
                      />
                      <input
                        id={"bankCode" + code}
                        type="radio"
                        name="bankId"
                        value={code}
                        onBlur={handleBlur}
                        onChange={e => {
                          handleBank(e, handleChange);
                        }}
                      />
                      <BankName>{(name || "").toLocaleLowerCase()}</BankName>
                    </BankContainer>
                  ))}
                </BanksContainer>
                {errors.bankId && touched.bankId && <Error>{errors.bankId}</Error>}
              </div>
              {!!values?.bankId && (
                <Alert dangerouslySetInnerHTML={{ __html: banksList?.find((bank) => bank?.code === values?.bankId)?.mensagem }} />
              )}
              <InputsContainer>
                <FieldContainer>
                  <TextField
                    label="Nome do titular da conta"
                    name="accountOwnerName"
                    type="text"
                    value={values.nome}
                    onBlur={handleBlur}
                    errorCondition={
                      errors.accountOwnerName && touched.accountOwnerName
                    }
                    errorMessage={errors.accountOwnerName}
                    LeadingIcon={() => <MdPermIdentity size={25} />}
                    onChange={e => {
                      handleName(e, handleChange);
                    }}
                  />
                </FieldContainer>

                <FieldContainer>
                  <FormikField
                    render={({ field }) => {
                      return (
                        <TextField
                          {...field}
                          label="CPF ou CNPJ *"
                          inputMode="text"
                          type="text"
                          value={values.accountDocument}
                          onChange={e => {
                            handleDocument(e, handleChange);
                          }}
                          onBlur={handleBlur}
                          errorCondition={
                            errors.accountDocument && touched.accountDocument
                          }
                          errorMessage={errors.accountDocument}
                          LeadingIcon={() => <MdAssignmentInd size={25} />}
                        />
                      );
                    }}
                    validate={validateDocument}
                    name="accountDocument"
                  />
                </FieldContainer>

                <FieldContainer>
                  <TextField
                    label="Agência"
                    name="branch"
                    inputMode="numeric"
                    type="text"
                    value={values.branch}
                    onBlur={handleBlur}
                    errorCondition={errors.branch && touched.branch}
                    errorMessage={errors.branch}
                    onChange={e => {
                      handleAgencia(e, handleChange);
                    }}
                  />
                </FieldContainer>

                <NumberDigit isFull={values?.bankId === '001'}>
                  <div>
                    <TextField
                      label="Número da conta"
                      name="account"
                      inputMode="numeric"
                      type="text"
                      value={values.account}
                      onBlur={handleBlur}
                      errorCondition={errors.account && touched.account}
                      errorMessage={errors.account}
                      onChange={e => {
                        handleNumber(e, handleChange);
                      }}
                    />
                    {values?.bankId === '001' &&
                      <span>Informe o número da conta sem o dígito.</span>
                    }
                  </div>

                  {values?.bankId !== '001' &&
                    <div>
                      <TextField
                        label="Dígito"
                        name="accountCheckDigit"
                        type="text"
                        value={values.accountCheckDigit}
                        onBlur={handleBlur}
                        errorCondition={
                          errors.accountCheckDigit && touched.accountCheckDigit
                        }
                        onChange={e => {
                          handleNumberDigit(e, handleChange);
                        }}
                      />
                    </div>
                  }
                </NumberDigit>
              </InputsContainer>

              <ButtonsContainer>
                <OutlinedButton
                  width="100%"
                  onClick={() => handleCancelAction()}
                  color={theme.colors.purple}
                >
                  {cancelButtonText}
                </OutlinedButton>
                <ContainedButton
                  type="submit"
                  continue
                  id="Avançar"
                  width="100%"
                  loading={loading}
                  disabled={!isValid || !dirty}
                >
                  {confirmButtonText}
                </ContainedButton>
              </ButtonsContainer>
            </FormContainer>
          )
        }}
      />
    </>
  );
};

export default DirectDebitForm;
